import { createContext, useContext, useMemo } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { getSkills, preloadPortfolioData } from '../firebase';
import { useLoading } from './LoadingContext';
import { logError } from '../utils/error-handler';

const SkillsContext = createContext(null);

export const SkillsProvider = ({ children }) => {
  const [skills, setSkills] = useState(getSkills() || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { finishTask, beginExpectedTasks } = useLoading();

  const loadSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    beginExpectedTasks(['skills']);

    try {
      await preloadPortfolioData();
      const skillsData = getSkills();
      setSkills(skillsData || []);
    } catch (err) {
      logError(err, 'SkillsProvider', {});
      setError(err);
    } finally {
      setLoading(false);
      finishTask('skills');
    }
  }, [beginExpectedTasks, finishTask]);

  const switchLanguageClientSide = useCallback(() => {
    beginExpectedTasks(['skills']);

    requestAnimationFrame(() => {
      try {
        const skillsData = getSkills();
        setSkills(skillsData || []);
      } catch (err) {
        logError(err, 'switchLanguageClientSide - Skills', {});
        loadSkills();
      } finally {
        finishTask('skills');
      }
    });
  }, [beginExpectedTasks, finishTask, loadSkills]);

  useEffect(() => {
    let isMounted = true;
    loadSkills();

    const handleLanguageChange = () => {
      if (isMounted) {
        switchLanguageClientSide();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      isMounted = false;
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [loadSkills, switchLanguageClientSide]);

  const value = useMemo(
    () => ({ skills, loading, error }),
    [skills, loading, error]
  );

  return (
    <SkillsContext.Provider value={value}>{children}</SkillsContext.Provider>
  );
};

export const useSkills = () => {
  const context = useContext(SkillsContext);
  if (!context) {
    throw new Error('useSkills must be used within SkillsProvider');
  }
  return context;
};
